(() => {
  if (customElements.get('ladona-trending-carousel')) return;

  class LadonaTrendingCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.carouselReady === 'true') return;

      this.track = this.querySelector('[data-carousel-track]');
      this.items = Array.from(this.querySelectorAll('[data-carousel-item]'));
      this.filters = Array.from(this.querySelectorAll('[data-carousel-filter]'));
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.currentElement = this.querySelector('[data-carousel-current]');
      this.totalElement = this.querySelector('[data-carousel-total]');
      this.progressElement = this.querySelector('[data-carousel-progress]');
      this.statusElement = this.querySelector('[data-carousel-status]');

      if (!this.track || this.items.length === 0) return;

      this.dataset.carouselReady = 'true';
      this.activeFilter = 'all';
      this.abortController = new AbortController();
      const eventOptions = { signal: this.abortController.signal };

      this.previousButton?.addEventListener('click', () => this.move(-1), eventOptions);
      this.nextButton?.addEventListener('click', () => this.move(1), eventOptions);
      this.track.addEventListener('scroll', () => this.requestUpdate(), {
        ...eventOptions,
        passive: true,
      });
      this.track.addEventListener('keydown', (event) => this.handleKeyboard(event), eventOptions);

      this.filters.forEach((filter) => {
        filter.addEventListener(
          'click',
          () => this.applyFilter(filter.dataset.carouselFilter || 'all'),
          eventOptions
        );
      });

      this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
      this.resizeObserver.observe(this.track);

      this.updateFilterAvailability();
      this.applyFilter('all', false);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      delete this.dataset.carouselReady;
    }

    normalize(value = '') {
      return value
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    categoriesFor(item) {
      return (item.dataset.categories || '')
        .split('|')
        .map((category) => this.normalize(category))
        .filter(Boolean);
    }

    itemMatches(item, filter) {
      return filter === 'all' || this.categoriesFor(item).includes(this.normalize(filter));
    }

    get visibleItems() {
      return this.items.filter((item) => !item.hidden);
    }

    updateFilterAvailability() {
      this.filters.forEach((filter) => {
        const value = filter.dataset.carouselFilter || 'all';
        if (value === 'all') return;
        filter.hidden = !this.items.some((item) => this.itemMatches(item, value));
      });
    }

    applyFilter(filter, announce = true) {
      this.activeFilter = this.normalize(filter) || 'all';

      this.items.forEach((item) => {
        item.hidden = !this.itemMatches(item, this.activeFilter);
      });

      this.filters.forEach((button) => {
        const isActive = this.normalize(button.dataset.carouselFilter || 'all') === this.activeFilter;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.track.scrollTo({ left: 0, behavior: 'auto' });
      if (announce) this.lastAnnouncedFilter = null;
      this.requestUpdate();
    }

    move(direction) {
      const firstItem = this.visibleItems[0];
      if (!firstItem) return;

      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const distance = firstItem.getBoundingClientRect().width + gap;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.track.scrollBy({
        left: direction * distance,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    handleKeyboard(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.track.scrollTo({ left: 0, behavior: 'auto' });
      } else if (event.key === 'End') {
        event.preventDefault();
        this.track.scrollTo({ left: this.track.scrollWidth, behavior: 'auto' });
      }
    }

    requestUpdate() {
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      this.updateFrame = requestAnimationFrame(() => this.update());
    }

    update() {
      const visibleItems = this.visibleItems;
      const total = visibleItems.length;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const scrollLeft = Math.max(0, this.track.scrollLeft);
      const atStart = scrollLeft <= 3;
      const atEnd = maxScroll <= 3 || scrollLeft >= maxScroll - 3;

      if (this.previousButton) this.previousButton.disabled = atStart;
      if (this.nextButton) this.nextButton.disabled = atEnd;

      let shownThrough = total;
      if (maxScroll > 3) {
        const viewportEnd = scrollLeft + this.track.clientWidth - 8;
        shownThrough = 1;
        visibleItems.forEach((item, index) => {
          const itemThreshold = item.offsetLeft + Math.min(item.offsetWidth * 0.45, 80);
          if (itemThreshold <= viewportEnd) shownThrough = index + 1;
        });
      }

      if (atEnd) shownThrough = total;
      shownThrough = Math.max(0, Math.min(shownThrough, total));

      if (this.currentElement) this.currentElement.textContent = this.formatCount(shownThrough);
      if (this.totalElement) this.totalElement.textContent = this.formatCount(total);
      if (this.progressElement) {
        const progress = total > 0 ? shownThrough / total : 0;
        this.progressElement.style.transform = `scaleX(${progress})`;
      }

      const progressChanged =
        this.lastAnnouncedThrough !== shownThrough ||
        this.lastAnnouncedTotal !== total ||
        this.lastAnnouncedFilter !== this.activeFilter;

      if (this.statusElement && progressChanged) {
        const activeButton = this.filters.find(
          (button) => this.normalize(button.dataset.carouselFilter || 'all') === this.activeFilter
        );
        const filterLabel = activeButton?.textContent.trim() || 'All';
        this.statusElement.textContent = `${filterLabel}: showing ${shownThrough} of ${total} flower ${
          total === 1 ? 'design' : 'designs'
        }.`;
        this.lastAnnouncedThrough = shownThrough;
        this.lastAnnouncedTotal = total;
        this.lastAnnouncedFilter = this.activeFilter;
      }
    }

    formatCount(number) {
      return String(number).padStart(2, '0');
    }
  }

  customElements.define('ladona-trending-carousel', LadonaTrendingCarousel);
})();
